import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { Select, SelectTypes } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';
import { useCloseable } from '@nhl/react-hooks/lib/useCloseable';

import CommonMessages from '~/core/commonMessages';
import { CountryCode } from '~/core/enums';
import { parseValueFromLocalizedAttribute } from '~/core/utils';
import { useLocationQuery } from '~/redux/ducks/location';

import BroadcastPanel from './components/broadcastPanel/BroadcastPanel';
import { BroadcastPanelData, Country, HowToWatchData } from './types';
import {
    getAreBroadcastersEmptyForAllPanels,
    getCountryOptionFromCountry,
    NETWORK_LINKS,
    OTHER_COUNTRIES_CODE,
} from './utils';

import {
    BroadcastsLink,
    DialogBackground,
    DialogContent,
    DialogOuterContainer,
    HeaderCloseIconContainer,
    InnerContent,
    ModalHeader,
    NoBroadcastsAvailableMessage,
    SelectBroadcastsContainer,
    StyledCloseIcon,
    Subtitle,
    ViewingGuideLabel,
} from './styles';

export type HowToWatchModalProps = {
    howToWatch: HowToWatchData | null;
    isLoading: boolean;
    onCloseModal: () => void;
};

const HowToWatchModal: FC<HowToWatchModalProps> = ({ howToWatch, onCloseModal, isLoading }) => {
    const { data: userCountryCode } = useLocationQuery({});

    const isDataLoading = isLoading || !(howToWatch && Object.keys(howToWatch)?.length);

    const intl = useIntl();

    const defaultCountry = useMemo<Country>(() => {
        let defaultCountryCode: CountryCode | typeof OTHER_COUNTRIES_CODE = CountryCode.UnitedStates;

        // if user is in US or Canada, default to that country
        if ([CountryCode.Canada, CountryCode.UnitedStates].includes(userCountryCode as CountryCode)) {
            defaultCountryCode = userCountryCode as CountryCode;
        } else {
            // otherwise, default to "Other Countries" Panel
            defaultCountryCode = OTHER_COUNTRIES_CODE;
        }
        return howToWatch?.countries?.find?.(
            (country: Country): boolean => country?.abbr === defaultCountryCode
        ) as Country;
    }, [howToWatch?.countries, userCountryCode]);

    const [selectedCountry, setSelectedCountry] = useState<Country>(defaultCountry);

    useEffect(() => {
        setSelectedCountry(defaultCountry);
    }, [defaultCountry]);

    const modalRef = useRef<HTMLDivElement>(null);

    useCloseable({
        closeableRef: modalRef,
        closeFunction: onCloseModal,
        shouldStopListening: false,
    });

    const dropdownCountryOptions: Array<SelectTypes.Option<{ value: Country | null }>> = useMemo(
        () =>
            isDataLoading
                ? []
                : (howToWatch?.countries?.map?.(
                      (country: Country): SelectTypes.Option<{ value: Country | null }> =>
                          getCountryOptionFromCountry(country)
                  ) as Array<SelectTypes.Option<{ value: Country | null }>>),
        [howToWatch?.countries, isDataLoading]
    );

    const panelsForCountry = useMemo<Array<BroadcastPanelData>>(
        () =>
            howToWatch?.panels?.filter((panel: BroadcastPanelData): boolean =>
                panel?.countries?.includes(selectedCountry?.abbr)
            ) as Array<BroadcastPanelData>,
        [selectedCountry?.abbr, howToWatch?.panels]
    );

    const areBroadcastersEmptyForAllPanels = useMemo(
        () => getAreBroadcastersEmptyForAllPanels(panelsForCountry),
        [panelsForCountry]
    );

    const areNoMessagesAvailable = !panelsForCountry?.length || areBroadcastersEmptyForAllPanels;

    return (
        <DialogOuterContainer>
            <DialogBackground />
            <DialogContent ref={modalRef} $isLoading={isDataLoading}>
                {isDataLoading ? (
                    <LoadingDisplay size={60} isInline isLoading />
                ) : (
                    <InnerContent>
                        <HeaderCloseIconContainer>
                            <ModalHeader>{parseValueFromLocalizedAttribute(howToWatch.title)}</ModalHeader>
                            <StyledCloseIcon onClick={onCloseModal} />
                        </HeaderCloseIconContainer>
                        <Subtitle>
                            <FormattedMessage {...CommonMessages.linkDescriptions.howToWatch} />
                        </Subtitle>
                        <ViewingGuideLabel>
                            {parseValueFromLocalizedAttribute(howToWatch.countrySelector)}
                        </ViewingGuideLabel>
                        <SelectBroadcastsContainer>
                            <Select
                                dropdownButtonAriaLabel={intl.formatMessage(
                                    CommonMessages.ariaLabels.select.country.dropdownButton
                                )}
                                optionsBoxAriaLabel={intl.formatMessage(
                                    CommonMessages.ariaLabels.select.country.optionsBox
                                )}
                                options={dropdownCountryOptions}
                                onOptionChange={(_, countryOption: SelectTypes.Option<{ value: Country }>) => {
                                    setSelectedCountry(countryOption.value);
                                }}
                                selectedOption={getCountryOptionFromCountry(selectedCountry)}
                            />
                            {areNoMessagesAvailable ? (
                                <>
                                    <NoBroadcastsAvailableMessage>
                                        <FormattedMessage {...CommonMessages.misc.noBroadcasts} />
                                    </NoBroadcastsAvailableMessage>
                                </>
                            ) : (
                                panelsForCountry.map(
                                    (panel: BroadcastPanelData): ReactNode => (
                                        <BroadcastPanel
                                            key={`panel-${panel.categoryId}`}
                                            panel={panel}
                                            selectedCountry={selectedCountry}
                                        />
                                    )
                                )
                            )}
                        </SelectBroadcastsContainer>
                        <BroadcastsLink href={parseValueFromLocalizedAttribute(NETWORK_LINKS.url)} target="_blank">
                            <FormattedMessage {...CommonMessages.linkDescriptions.localBroadcastArea} />
                        </BroadcastsLink>
                    </InnerContent>
                )}
            </DialogContent>
        </DialogOuterContainer>
    );
};

export default HowToWatchModal;
